import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Upload, X, FileText, PenTool, Wand2, Palette,
} from "lucide-react";import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const PRODUCT_CATEGORIES = [
  "T-Shirts", "Hoodies", "Hats", "Tumblers", "Mugs", "Bags/Totes",
  "Decals/Stickers", "Signs", "Party Items", "Business/Promotional",
  "QR Code Products", "Digital Products", "Other",
];
const PLACEMENTS = ["Front", "Back", "Front and Back", "Left Chest", "Right Chest", "Sleeve", "Hat Front", "Other"];
const FILE_TYPES = ["Finished Artwork", "Logo", "Personal Photo", "QR Code", "Reference Image", "Other"];
const DESIGN_SERVICES = ["Production Only", "Design Creation", "Design Modification", "I'm Not Sure"];
const EVENT_TYPES = ["Birthday", "Wedding", "Graduation", "Family Reunion", "Business Event", "Holiday", "School Event", "Other", "No specific event"];
const ACCEPT = ".png,.jpg,.jpeg,.pdf,.svg,.eps,.ai,.zip";

const START_CARDS = [
  { key: "Production Only", title: "I Have My Design", desc: "Print-ready artwork is done — you just need it produced.", Icon: FileText },
  { key: "Design Creation", title: "I Need a Design Created", desc: "Start from scratch with our team's help bringing your idea to life.", Icon: Wand2 },
  { key: "Design Modification", title: "I Have a Design That Needs Changes", desc: "Tweak, resize, recolor, or refresh an existing design.", Icon: PenTool },
];

const EMPTY = {
  full_name: "", email: "", phone: "",
  returning_customer: false, quote_invoice_number: "",
  product_category: "", specific_product: "", quantity: 1,
  multiple_sizes_colors: false, sizes_colors_detail: "",
  design_description: "", design_placements: [],
  file_types: [],
  design_service: "",
  event_types: [], event_date: "", need_by_date: "",
  special_instructions: "",
  agree_image_rights: false, agree_no_guarantee: false, agree_design_fee: false, agree_payment_before: false,
};

function Section({ n, title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-silver-light p-6" data-testid={`section-${n}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-7 h-7 rounded-full bg-navy-dark text-white text-xs font-bold flex items-center justify-center">{n}</span>
        <h3 className="font-serif text-xl text-navy-dark">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function CustomOrderRequest() {
  const { user } = useAuth();
  const [started, setStarted] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: f.full_name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleArr = (key, val) => setForm((f) => {
    const arr = f[key] || [];
    return { ...f, [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val] };
  });

  const startWith = (service) => {
    set({ design_service: service });
    setStarted(true);
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/custom-requests/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((p) => [...p, data]);
      } catch (err) {
        toast.error(`${file.name}: ${formatDetail(err.response?.data?.detail) || "Upload failed"}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((p) => p.filter((_, i) => i !== idx));

  const allAgreed = form.agree_image_rights && form.agree_no_guarantee && form.agree_design_fee && form.agree_payment_before;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.design_service) { toast.error("Please choose a design service option."); return; }
    if (!allAgreed) { toast.error("Please check all four agreement boxes to submit."); return; }
    setSubmitting(true);
    try {
      await api.post("/custom-requests", {
        ...form,
        quantity: Number(form.quantity) || 1,
        files: files.map((f) => ({ url: f.url, filename: f.filename, content_type: f.content_type, size: f.size, storage_path: f.storage_path })),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  const humanSize = (b) => !b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="custom-request-success">
        <CheckCircle2 size={56} className="mx-auto text-navy-royal" />
        <h1 className="font-serif text-4xl text-navy-dark mt-4">We've received your request!</h1>
        <p className="text-navy/70 mt-3">Your custom order and files have been submitted. We'll review your information and contact you if additional details are needed. Watch your email for your quote, next steps, and payment information.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/account" className="rounded-md bg-navy-dark text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.15em]">View My Account</Link>
          <Link to="/shop" className="rounded-md border border-navy-dark text-navy-dark px-6 py-3 text-xs font-bold uppercase tracking-[0.15em]">Back to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" ref={topRef} data-testid="custom-request">
      {/* Landing */}
      <div className="text-center">
        <div className="font-script text-3xl text-navy-royal">Custom Orders</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-dark mt-1">Create Something That's Uniquely Yours</h1>
        <p className="text-navy/60 mt-3 max-w-xl mx-auto">Tell us where you're starting from and share your details — we'll take it from there.</p>
      </div>

      <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="start-cards">
        {START_CARDS.map(({ key, title, desc, Icon }) => {
          const active = form.design_service === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => startWith(key)}
              className={`text-left rounded-2xl border p-5 transition-all hover:-translate-y-0.5 hover:shadow-md ${active ? "border-navy-royal ring-2 ring-navy-royal/20 bg-navy-royal/[0.03]" : "border-silver-light bg-white"}`}
              data-testid={`start-card-${key.replace(/\s/g, "-").toLowerCase()}`}
            >
              <Icon size={22} className="text-navy-royal" />
              <div className="font-serif text-lg text-navy-dark mt-3">{title}</div>
              <div className="text-sm text-navy/60 mt-1">{desc}</div>
            </button>
          );
        })}
      </div>

      {!started && (
        <div className="mt-8 text-center">
          <Button onClick={() => startWith(form.design_service || "")} className="bg-navy-dark hover:bg-navy-royal text-white rounded-full px-10 py-6 uppercase tracking-[0.15em] text-xs font-bold" data-testid="start-custom-order">
            Start My Custom Order
          </Button>
        </div>
      )}

      {started && (
        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="custom-request-form">
          {/* 1. Customer Information */}
          <Section n={1} title="Customer Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full name *</Label><Input required value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} data-testid="cr-name" /></div>
              <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="cr-email" /></div>
              <div><Label>Phone *</Label><Input required value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="cr-phone" /></div>
              <div><Label>Quote / Invoice # (optional)</Label><Input value={form.quote_invoice_number} onChange={(e) => set({ quote_invoice_number: e.target.value })} data-testid="cr-quote-number" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="mb-0">Returning customer?</Label>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold">
                <button type="button" onClick={() => set({ returning_customer: true })} className={`px-4 py-1.5 rounded-full ${form.returning_customer ? "bg-navy-dark text-white" : "text-navy/60"}`} data-testid="cr-returning-yes">Yes</button>
                <button type="button" onClick={() => set({ returning_customer: false })} className={`px-4 py-1.5 rounded-full ${!form.returning_customer ? "bg-navy-dark text-white" : "text-navy/60"}`} data-testid="cr-returning-no">No</button>
              </div>
            </div>
          </Section>

          {/* 2. What Are You Ordering */}
          <Section n={2} title="What Are You Ordering">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Product category *</Label>
                <select required value={form.product_category} onChange={(e) => set({ product_category: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="cr-category">
                  <option value="" disabled>Select a category…</option>
                  {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Specific product</Label><Input value={form.specific_product} onChange={(e) => set({ specific_product: e.target.value })} placeholder="e.g. Bella+Canvas 3001 tee" data-testid="cr-specific" /></div>
              <div><Label>Quantity *</Label><Input required type="number" min={1} value={form.quantity} onChange={(e) => set({ quantity: e.target.value })} data-testid="cr-quantity" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="mb-0">Multiple sizes / colors?</Label>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold">
                <button type="button" onClick={() => set({ multiple_sizes_colors: true })} className={`px-4 py-1.5 rounded-full ${form.multiple_sizes_colors ? "bg-navy-dark text-white" : "text-navy/60"}`} data-testid="cr-multi-yes">Yes</button>
                <button type="button" onClick={() => set({ multiple_sizes_colors: false })} className={`px-4 py-1.5 rounded-full ${!form.multiple_sizes_colors ? "bg-navy-dark text-white" : "text-navy/60"}`} data-testid="cr-multi-no">No</button>
              </div>
            </div>
            {form.multiple_sizes_colors && (
              <div><Label>Sizes / colors breakdown</Label><Textarea rows={3} value={form.sizes_colors_detail} onChange={(e) => set({ sizes_colors_detail: e.target.value })} placeholder="e.g. 3 Small Black, 5 Large Navy, 2 XL White" data-testid="cr-multi-detail" /></div>
            )}
          </Section>

          {/* 3. Design Description */}
          <Section n={3} title="Design Description">
            <div>
              <Label>Describe your design *</Label>
              <Textarea required rows={5} value={form.design_description} onChange={(e) => set({ design_description: e.target.value })}
                placeholder="Example: I'd like a navy tee with 'Faith Over Fear' in a bold script across the front chest, plus a small cross on the left sleeve. Colors should be gold and white." data-testid="cr-description" />
            </div>
            <div>
              <Label>Design placement</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PLACEMENTS.map((p) => (
                  <label key={p} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer ${form.design_placements.includes(p) ? "border-navy-royal bg-navy-royal/5" : "border-silver-light"}`} data-testid={`cr-placement-${p.replace(/\s/g, "-").toLowerCase()}`}>
                    <input type="checkbox" checked={form.design_placements.includes(p)} onChange={() => toggleArr("design_placements", p)} className="accent-navy-royal" />
                    {p}
                  </label>
                ))}
              </div>
            </div>
          </Section>

          {/* 4. File Upload */}
          <Section n={4} title="Upload Your Files">
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-silver-light py-8 cursor-pointer hover:border-navy-royal transition-colors" data-testid="cr-upload-label">
              <Upload size={22} className="text-navy-royal" />
              <span className="text-sm font-semibold text-navy-dark">{uploading ? "Uploading…" : "Click to upload files"}</span>
              <span className="text-xs text-navy/50">PNG, JPG, PDF, SVG, EPS, AI, ZIP — up to 25 MB each</span>
              <input type="file" multiple accept={ACCEPT} onChange={onFiles} disabled={uploading} className="hidden" data-testid="cr-upload-input" />
            </label>
            {files.length > 0 && (
              <ul className="space-y-2" data-testid="cr-file-list">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-silver-light px-3 py-2 text-sm" data-testid={`cr-file-${i}`}>
                    <span className="flex items-center gap-2 min-w-0"><FileText size={15} className="text-navy-royal shrink-0" /><span className="truncate text-navy-dark">{f.filename}</span><span className="text-xs text-navy/40 shrink-0">{humanSize(f.size)}</span></span>
                    <button type="button" onClick={() => removeFile(i)} className="p-1 rounded hover:bg-cpr/10 text-cpr shrink-0" data-testid={`cr-file-remove-${i}`}><X size={15} /></button>
                  </li>
                ))}
              </ul>
            )}
            <div>
              <Label>What kind of files are these?</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {FILE_TYPES.map((t) => (
                  <label key={t} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer ${form.file_types.includes(t) ? "border-navy-royal bg-navy-royal/5" : "border-silver-light"}`} data-testid={`cr-filetype-${t.replace(/\s/g, "-").toLowerCase()}`}>
                    <input type="checkbox" checked={form.file_types.includes(t)} onChange={() => toggleArr("file_types", t)} className="accent-navy-royal" />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          </Section>

          {/* 5. Design Service */}
          <Section n={5} title="Design Service">
            <div className="grid sm:grid-cols-2 gap-2">
              {DESIGN_SERVICES.map((s) => (
                <label key={s} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2.5 cursor-pointer ${form.design_service === s ? "border-navy-royal bg-navy-royal/5" : "border-silver-light"}`} data-testid={`cr-service-${s.replace(/[\s']/g, "-").toLowerCase()}`}>
                  <input type="radio" name="design_service" checked={form.design_service === s} onChange={() => set({ design_service: s })} className="accent-navy-royal" />
                  {s}
                </label>
              ))}
            </div>
            <p className="text-xs text-navy/60 flex items-start gap-2"><Palette size={14} className="text-navy-royal mt-0.5 shrink-0" /> Design fees may apply and will be communicated to you before any work begins.</p>
          </Section>

          {/* 6. Event and Deadline */}
          <Section n={6} title="Event & Deadline">
            <div>
              <Label>Event type</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {EVENT_TYPES.map((ev) => (
                  <label key={ev} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer ${form.event_types.includes(ev) ? "border-navy-royal bg-navy-royal/5" : "border-silver-light"}`} data-testid={`cr-event-${ev.replace(/\s/g, "-").toLowerCase()}`}>
                    <input type="checkbox" checked={form.event_types.includes(ev)} onChange={() => toggleArr("event_types", ev)} className="accent-navy-royal" />
                    {ev}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Event date (optional)</Label><Input type="date" value={form.event_date} onChange={(e) => set({ event_date: e.target.value })} data-testid="cr-event-date" /></div>
              <div><Label>Need it by *</Label><Input required type="date" value={form.need_by_date} onChange={(e) => set({ need_by_date: e.target.value })} data-testid="cr-need-by" /></div>
            </div>
          </Section>

          {/* 7. Special Instructions */}
          <Section n={7} title="Special Instructions">
            <Textarea rows={4} value={form.special_instructions} onChange={(e) => set({ special_instructions: e.target.value })} placeholder="Anything else we should know? Colors to avoid, packaging preferences, delivery notes, etc." data-testid="cr-special" />
          </Section>

          {/* 8. Customer Agreement */}
          <Section n={8} title="Customer Agreement">
            {[
              { key: "agree_image_rights", label: "I confirm I have the rights to use any images, logos, or artwork I've submitted." },
              { key: "agree_no_guarantee", label: "I understand submitting a request does not guarantee acceptance of the order or a specific delivery date." },
              { key: "agree_design_fee", label: "I understand design fees may apply and will be communicated to me before work begins." },
              { key: "agree_payment_before", label: "I understand payment is required before production begins." },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-start gap-3 text-sm text-navy/80" data-testid={`cr-${key.replace(/_/g, "-")}`}>
                <input type="checkbox" checked={form[key]} onChange={(e) => set({ [key]: e.target.checked })} className="mt-1 accent-navy-royal" />
                {label} <span className="text-cpr">*</span>
              </label>
            ))}
            <Button type="submit" disabled={submitting || uploading} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 uppercase tracking-[0.15em] text-xs font-bold mt-2" data-testid="cr-submit">
              {submitting ? "Submitting…" : "Submit My Custom Order"}
            </Button>
          </Section>
        </form>
      )}
    </div>
  );
}
